/**
 * When the work comes in, where it comes from, and what nobody could fill.
 *
 * Everything else in this area describes what happened. Unfilled demand is the number that
 * says what to do next: an order the search gave up on is a passenger who rang somebody else,
 * and it is the only figure a firm can act on by putting another car on the road at eight on
 * a Friday. So it is a headline of its own, shown against the same axes as everything else,
 * and never folded into cancellations — which are a different problem with a different
 * answer, and have their own table below.
 *
 * The week starts where the firm's week starts rather than on a Monday somebody assumed,
 * which is why this page asks for the duty settings the shift book already governs. A firm
 * running Sunday to Saturday reading its busiest evening off the wrong row would be a quiet
 * wrong answer, which is the worst kind.
 */

import { useTranslation } from 'react-i18next';

import { useTenantFormats } from '../shell/formats';
import {
  Card,
  CardHeader,
  Count,
  EmptyState,
  PageHeader,
  StatTile,
  Table,
  Td,
  Th,
  Tr,
} from '../ui';
import { daysOfWeek, reportsApi } from './api';
import { DemandHeatmap } from './DemandHeatmap';
import { ExportButton } from './ExportButton';
import { useDutySettings, useReport, type ReportPageProps } from './frame';
import { PeriodRow } from './PeriodRow';

export function DemandPage({ frame }: ReportPageProps) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const settings = useDutySettings();
  const { data: report } = useReport(frame, (period) => reportsApi.demand(period));

  // Loudest first, as it has always been. The zones and the cancellations keep the order the
  // server sent them in; only the sources arrive as a map and have to be given one.
  const sources = Object.entries(report?.sources ?? {}).sort(([, left], [, right]) => right - left);

  return (
    <>
      <PageHeader
        title={t('nav.reports.demand')}
        description={t('reports.about.demand')}
        actions={<ExportButton frame={frame} path="/api/tenant/reports/demand" />}
      />

      <PeriodRow frame={frame} />

      {report === null ? (
        <Card>
          <EmptyState>{t('reports.loading')}</EmptyState>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Card>
              <StatTile label={t('reports.demand.orders')} value={formats.count(report.orders)} />
            </Card>
            <Card>
              <StatTile
                label={t('reports.demand.completed')}
                value={formats.count(report.completed)}
              />
            </Card>
            <Card>
              {/* The one figure with a colour on it. Nothing else on this report is
                  something to do; this is a car that was not there. */}
              <StatTile
                label={t('reports.demand.unfilled')}
                value={formats.count(report.unfilled)}
                tone={report.unfilled > 0 ? 'warn' : 'neutral'}
              />
            </Card>
          </div>

          <Card>
            <CardHeader title={t('reports.demand.byHour')}>
              {/* Stated in words as well as shaded, and beside the picture it describes:
                  the one sentence somebody repeats in a meeting has to be readable without
                  the grid in front of them. */}
              {report.busiestHour !== null && report.busiestDayOfWeek !== null && (
                <span className="text-secondary text-ink-3 font-normal">
                  {t('reports.demand.busiest', {
                    day: formats.weekday(report.busiestDayOfWeek),
                    hour: `${String(report.busiestHour).padStart(2, '0')}:00`,
                  })}
                </span>
              )}
            </CardHeader>

            {/* Twenty-four columns is wider than the page on a small screen and must scroll
                inside its own card: the console's own layout never scrolls sideways. The
                padding is on the outside of the scrolling box rather than on it, because a
                sticky column pins itself to the scrollport rather than to the padding — put
                them the other way round and the shaded cells slide out from under the
                weekday names. */}
            <div className="p-3">
              <div className="overflow-x-auto">
                <DemandHeatmap
                  report={report}
                  weekStartsOn={daysOfWeek.indexOf(settings?.weekStartsOn ?? 'Monday')}
                />
              </div>
            </div>
          </Card>

          <p className="text-secondary text-ink-3 m-0">{t('reports.demand.unfilledLegend')}</p>

          <Card>
            <CardHeader title={t('reports.demand.byZone')}>
              <Count>{formats.count(report.zones.length)}</Count>
            </CardHeader>

            <div className="overflow-x-auto">
              <Table>
                <thead>
                  <tr>
                    <Th>{t('reports.demand.zone')}</Th>
                    <Th numeric>{t('reports.demand.orders')}</Th>
                    <Th numeric>{t('reports.demand.unfilled')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {report.zones.map((zone) => (
                    <Tr key={zone.zoneId ?? 'outside'}>
                      <Td header>{zone.displayName ?? t('reports.demand.outsideEveryZone')}</Td>
                      <Td numeric>{formats.count(zone.orders)}</Td>
                      <Td numeric>{formats.count(zone.unfilled)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2 items-start">
            <Card className="min-w-0">
              <CardHeader title={t('reports.demand.cancellations')} />

              <div className="overflow-x-auto">
                <Table tight>
                  <thead>
                    <tr>
                      <Th>{t('reports.demand.cancelledBy')}</Th>
                      <Th>{t('reports.demand.afterAssignment')}</Th>
                      <Th numeric>{t('reports.demand.orders')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.cancellations.map((row) => (
                      <Tr key={`${row.actor}-${String(row.afterAssignment)}`}>
                        <Td header>{t(`actors.${row.actor}`)}</Td>
                        <Td>
                          {row.afterAssignment
                            ? t('reports.demand.afterACarWasSent')
                            : t('reports.demand.beforeACarWasSent')}
                        </Td>
                        <Td numeric>{formats.count(row.orders)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card>

            <Card className="min-w-0">
              <CardHeader title={t('reports.demand.sources')} />

              <div className="overflow-x-auto">
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('reports.demand.source')}</Th>
                      <Th numeric>{t('reports.demand.orders')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map(([source, count]) => (
                      <Tr key={source}>
                        <Td header>{t(`reports.sources.${source}`)}</Td>
                        <Td numeric>{formats.count(count)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card>
          </div>
        </>
      )}
    </>
  );
}
